import {
  useOrderCenterLogisticGenerateRemission,
  useOrderCenterSaleOrderDetail,
  useOrderTrackerList,
} from '@/services'
import { useTranslation } from 'react-i18next'

import { ROUTES_ADMIN } from '@/router'
import { Button, Text } from '@mercai/clever'
import { Link, useParams } from 'react-router-dom'

import { MarginMetrics, SelectSaleOrderStatus, Tabs } from '@/components'
import { appOrderLink } from '@/constants'
import { useParamsState } from '@/helpers'
import { FinanceProfilePaymentFormsEnum } from '@/types'
import { ReactNode } from 'react'
import {
  TabBuyerProfile,
  TabClientDeposit,
  TabComments,
  TabCredit,
  TabDocuments,
  TabGeneral,
  TabHistory,
  TabLogistic,
  TabProducts,
  TabSuplier,
} from './components'

import styles from './show.module.css'

export const OrderCenterPageShow = () => {
  const { t } = useTranslation('screenOrderCenterPage')

  const { uuid } = useParams()

  const [params, setParams] = useParamsState({
    tab: 'general',
  })

  const { data, isLoading } = useOrderCenterSaleOrderDetail(uuid)

  const { data: dataTracker } = useOrderTrackerList({
    params: { search: `${data?.data?.reference}` },
  })

  const { mutateAsync: generateRemissionAsync, isPending } =
    useOrderCenterLogisticGenerateRemission()

  const isCredit =
    data?.data?.payments?.[0]?.form === FinanceProfilePaymentFormsEnum.credit ||
    (data?.data?.creditOrders?.length || 0) > 0

  const handleGenerateRemissionFile = async () => {
    const { data } = await generateRemissionAsync(uuid || '')

    if (!data) {
      return
    }

    const url = window.URL.createObjectURL(data)

    window.open(url, '_blank')

    setTimeout(() => {
      window.URL.revokeObjectURL(url)
    }, 10000)
  }

  const tabsComponents: Record<string, ReactNode> = {
    general: <TabGeneral isLoading={isLoading} saleOrder={data?.data} />,
    products: <TabProducts isLoading={isLoading} saleOrder={data?.data} />,
    'client-deposit': (
      <TabClientDeposit
        isLoading={isLoading}
        saleOrder={data?.data}
        isActive={params.tab === 'client-deposit'}
      />
    ),
    supplier: (
      <TabSuplier
        isLoading={isLoading}
        saleOrderUuid={data?.data?.uuid}
        saleOrderReference={data?.data?.reference}
        supplierUuid={data?.data?.shipments?.[0]?.supplier?.uuid}
        saleOrderDestinationAddress={
          data?.data?.shipments?.[0]?.destinationAddress
        }
      />
    ),
    logistic: <TabLogistic isLoading={isLoading} saleOrder={data?.data} />,
    profile: <TabBuyerProfile isLoading={isLoading} saleOrder={data?.data} />,
    comments: (
      <TabComments isLoading={isLoading} saleOrderUuid={data?.data?.uuid} />
    ),
    documents: <TabDocuments isLoading={isLoading} saleOrder={data?.data} />,
    history: (
      <TabHistory isLoading={isLoading} saleOrderUuid={data?.data?.uuid} />
    ),
    ...(isCredit
      ? { credit: <TabCredit isLoading={isLoading} saleOrder={data?.data} /> }
      : {}),
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link
            to={ROUTES_ADMIN.ORDER_CENTER.ALL.fullPath()}
          >{`${t('orderCenterShowBreadcrumTitle')}`}</Link>
          {` / `}
          <span>{data?.data?.reference}</span>
        </Text>
        <div className={styles['header-content']}>
          <div>
            <div className={styles['sub-title']}>
              <Text variant="title-large">{data?.data?.reference}</Text>
              <SelectSaleOrderStatus saleOrder={data?.data} />
            </div>
            <Text
              variant="text-base"
              className={styles['sub-title-instructions']}
            >
              {t('orderCenterShowSubtitle')}
            </Text>
          </div>
          <div className={styles['header-actions']}>
            <Button
              variant="secondary"
              href={`${ROUTES_ADMIN.ANALYTICS.ORDER_TRACKER.fullPath()}?search=${data?.data?.reference}`}
            >
              {t('orderCenterShowTrackerButton')}
            </Button>
            <Button href={appOrderLink(data?.data?.uuid || '')} target="_blank">
              {t('orderCenterShowAppButton')}
            </Button>

            <Button
              onClick={() => handleGenerateRemissionFile()}
              isActive={!isPending}
              disabled={isPending}
              variant="success-light"
            >
              {t('orderCenterShowRemissionLinkButton')}
            </Button>
          </div>
        </div>
      </header>

      <Tabs
        items={[
          {
            title: t('orderCenterShowTabGeneral'),
            isActive: !params.tab || params.tab === 'general',
            onSelect: () => setParams({ tab: 'general' }),
          },
          {
            title: t('orderCenterShowTabProducts'),
            isActive: !params.tab || params.tab === 'products',
            onSelect: () => setParams({ tab: 'products' }),
          },
          {
            title: t('orderCenterShowTabCustomerDeposits'),
            isActive: !params.tab || params.tab === 'client-deposit',
            onSelect: () => setParams({ tab: 'client-deposit' }),
          },
          {
            title: t('orderCenterShowTabSupplier'),
            isActive: !params.tab || params.tab === 'supplier',
            onSelect: () => setParams({ tab: 'supplier' }),
          },
          {
            title: t('orderCenterShowTabLogistics'),
            isActive: !params.tab || params.tab === 'logistic',
            onSelect: () => setParams({ tab: 'logistic' }),
          },
          {
            title: t('orderCenterShowTabProfile'),
            isActive: !params.tab || params.tab === 'profile',
            onSelect: () => setParams({ tab: 'profile' }),
          },
          {
            title: t('orderCenterShowTabDocuments'),
            isActive: !params.tab || params.tab === 'documents',
            onSelect: () => setParams({ tab: 'documents' }),
          },
          {
            title: t('orderCenterShowTabComments'),
            isActive: !params.tab || params.tab === 'comments',
            onSelect: () => setParams({ tab: 'comments' }),
          },
          {
            title: t('orderCenterShowTabHistory'),
            isActive: !params.tab || params.tab === 'history',
            onSelect: () => setParams({ tab: 'history' }),
          },
          ...(isCredit
            ? [
                {
                  title: t('orderCenterShowTabCredit'),
                  isActive: !params.tab || params.tab === 'credit',
                  onSelect: () => setParams({ tab: 'credit' }),
                },
              ]
            : []),
        ]}
      />

      <div className={styles.margin}>
        <MarginMetrics
          incomplete={!dataTracker}
          cm3Margin={
            dataTracker?.data?.results?.[0]?.grossProfitData?.orderCm3Margin
              ? Number(
                  dataTracker?.data?.results?.[0]?.grossProfitData
                    ?.orderCm3Margin,
                )
              : 0
          }
          grossMargin={
            dataTracker?.data?.results?.[0]?.grossProfitData?.orderGrossMargin
              ? Number(
                  dataTracker?.data?.results?.[0]?.grossProfitData
                    ?.orderGrossMargin,
                )
              : 0
          }
        />
      </div>

      {tabsComponents?.[params.tab || 'general']}
    </div>
  )
}
